import React, { useState, useEffect, useRef } from 'react';
import { TrendingUp, Users, Zap, Award, Briefcase, X, ArrowRight, ChevronDown } from 'lucide-react';
import PageHeader from '../../components/PageHeader/PageHeader';
import './Careers.css';

import teamImage from '../../assets/pexels-yankrukov-9072304.jpg';
import officeImage from '../../assets/pexels-tima-miroshnichenko-5380666.jpg';

const RevealOnScroll = ({ children, className = '', delay = 0 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const domRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (let i = 0; i < entries.length; i++) {
          setIsVisible(entries[i].isIntersecting);
        }
      },
      { threshold: 0.1 }
    );
    const current = domRef.current;
    if (current) observer.observe(current);
    return () => { if (current) observer.unobserve(current); };
  }, []);

  return (
    <div ref={domRef} className={`reveal-wrapper ${isVisible ? 'is-visible' : ''} ${className}`} style={{ transitionDelay: `${delay}s` }}>
      {children}
    </div>
  );
};

export default function Careers() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [expandedJobId, setExpandedJobId] = useState(null);

  // Set your application window dates here (Format: YYYY-MM-DD)
  const jobsData = [
    {
      id: 1,
      title: "Web & Mobile Development Internship",
      type: "Internship - Remote/Hybrid",
      openDate: "2025-11-01", 
      closeDate: "2026-02-25",
      summary: "Join our dynamic development team to learn and build real-world React and Flutter applications...",
      fullDesc: "Join our dynamic development team to learn and build real-world React and Flutter applications. You will be paired with senior developers and work on live projects including CRM systems, disciplinary portals, and digital RSVP platforms. Strong foundational knowledge in JavaScript and a passion for coding is required.",
      requirements: ["Computer Science Degree/Diploma or active enrollment", "Basic Knowledge of React or Flutter", "Git & GitHub proficiency", "Eagerness to learn and adapt"]
    }
  ];

  // Lock scrolling when modal is open
  useEffect(() => {
    if (isModalOpen) {
      document.body.classList.add('modal-open');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  const toggleJobExpansion = (id) => {
    setExpandedJobId(expandedJobId === id ? null : id);
  };

  const generateMailtoLink = (jobTitle) => {
    const subject = encodeURIComponent(`Application for: ${jobTitle}`);
    const body = encodeURIComponent(`[PLEASE ATTACH YOUR CV, ALONG WITH YOUR ID COPY AND ACADEMIC TRANSCRIPTS/PORTFOLIO]\n\nGreetings, I hope this email finds you well.\n\nI would like to submit an application for the ${jobTitle} position.\n\nName: \nSurname: \nHighest Qualification: \nYears of Experience: \nLink to Portfolio/GitHub (if applicable): \n\nMotivation / Cover Letter:\n[Write a brief paragraph on why you are the best fit for this role, highlighting your core skills, experience, and what makes you stand out.]`);
    return `mailto:info@singularitytech.co.za?subject=${subject}&body=${body}`;
  };

  // Helper to format dates for display
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const jobElements = [];
  const currentDate = new Date();

  for (let i = 0; i < jobsData.length; i++) {
    const job = jobsData[i];
    const isExpanded = expandedJobId === job.id;
    
    // Check if the current date falls between the open and close dates
    const openDateObj = new Date(job.openDate);
    // Set close date to end of the day to ensure full day inclusion
    const closeDateObj = new Date(job.closeDate);
    closeDateObj.setHours(23, 59, 59, 999);
    
    const isOpen = currentDate >= openDateObj && currentDate <= closeDateObj;
    
    jobElements.push(
      <div 
        key={job.id} 
        className={`job-card ${isExpanded ? 'expanded' : ''}`}
        onClick={() => toggleJobExpansion(job.id)}
      >
        <div className="job-card-header">
          <div>
            <h3 className="job-title">{job.title}</h3>
            <span className="job-date-info">
              Applications: {formatDate(job.openDate)} - {formatDate(job.closeDate)}
            </span>
            <p className="job-summary">{isExpanded ? job.fullDesc : job.summary}</p>
          </div>
          <div className="job-badges">
            <span className="job-type">{job.type}</span>
            <span className={`job-status ${isOpen ? 'open' : 'closed'}`}>
              {isOpen ? 'Open' : 'Closed'}
            </span>
            <ChevronDown size={20} style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: '0.3s', color: 'var(--text-gray)' }} />
          </div>
        </div>

        {isExpanded && (
          <div className="job-full-details" onClick={(e) => e.stopPropagation()}>
            <h4>Requirements & Responsibilities:</h4>
            <ul>
              {job.requirements.map((req, idx) => (
                <li key={idx}>{req}</li>
              ))}
            </ul>
            
            {isOpen ? (
              <a 
                href={generateMailtoLink(job.title)}
                className="btn-primary" 
                style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', textDecoration: 'none', marginTop: '10px' }}
              >
                Apply via Email <ArrowRight size={18} />
              </a>
            ) : (
              <p className="closed-message">
                Applications for this position are currently closed. Please check back during the next application window.
              </p>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="careers-page">
      {/* Updated the Title prop to use the split Green & White styling */}
      <PageHeader 
        title={<>Careers & <br/><span>Opportunities</span></>} 
        description="Join the Singularity Tech team. We are always looking for innovative minds in software development, physical security, and IT infrastructure to help us build the future."
      />

      <div className="careers-main-content">
        <div className="container">
          
          <div className="careers-intro-grid">
            <RevealOnScroll className="careers-text">
              <span className="section-label">JOIN THE TEAM</span>
              <h2>Shape the Future of Infrastructure & Software</h2>
              <p className="intro-text">
                At Singularity, <span>we are passionate about pushing the boundaries of technology</span> and driving innovation forward. 
              </p>
              <p className="intro-text">
                As a member of our team, you will have the opportunity to work with cutting-edge technology, deploy vital physical security systems, and collaborate with some of the brightest developers and engineers in the industry.
              </p>
            </RevealOnScroll>

            <RevealOnScroll className="careers-images" delay={0.2}>
              <img src={teamImage} alt="Team collaboration" className="careers-img-1" />
              <img src={officeImage} alt="Office setup" className="careers-img-2" />
            </RevealOnScroll>
          </div>

          <div className="benefits-section">
            <RevealOnScroll>
              <span className="section-label">CULTURE & BENEFITS</span>
              <h2 style={{ fontSize: '36px', fontWeight: 'bold', color: 'var(--text-dark)' }}>Why Work With Us</h2>
            </RevealOnScroll>
            
            <div className="benefits-grid">
              <RevealOnScroll delay={0.1} className="benefit-box">
                <TrendingUp className="benefit-icon" size={40} />
                <h4 style={{ color: 'var(--text-dark)' }}>Dynamic Environment</h4>
                <p style={{ color: 'var(--text-gray)' }}>A fast-paced workspace that adapts to modern tech trends.</p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.2} className="benefit-box">
                <Award className="benefit-icon" size={40} />
                <h4 style={{ color: 'var(--text-dark)' }}>Rapid Growth</h4>
                <p style={{ color: 'var(--text-gray)' }}>Direct opportunities for professional growth and leadership advancement.</p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.3} className="benefit-box">
                <Users className="benefit-icon" size={40} />
                <h4 style={{ color: 'var(--text-dark)' }}>Inclusive Culture</h4>
                <p style={{ color: 'var(--text-gray)' }}>A highly collaborative and inclusive team structure built on respect.</p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.4} className="benefit-box">
                <Zap className="benefit-icon" size={40} />
                <h4 style={{ color: 'var(--text-dark)' }}>Latest Tech Stack</h4>
                <p style={{ color: 'var(--text-gray)' }}>Hands-on exposure to advanced network hardware and modern software frameworks.</p>
              </RevealOnScroll>
            </div>
          </div>

          <RevealOnScroll>
            <div className="cta-box">
              <Briefcase size={48} style={{ color: 'var(--brand-green)', marginBottom: '20px', position: 'relative', zIndex: 2 }} />
              <h3>Career Opportunities</h3>
              <p>We are committed to creating a diverse and inclusive workplace where everyone can thrive. Explore our open roles for working totally remote, from the field, or right here in the office.</p>
              <button className="btn-primary" onClick={() => setIsModalOpen(true)}>View Open Roles</button>
            </div>
          </RevealOnScroll>
        </div>
      </div>

      {/* --- Open Roles Modal --- */}
      <div className={`jobs-modal-overlay ${isModalOpen ? 'active' : ''}`} onClick={() => setIsModalOpen(false)}>
        <div className="jobs-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="jobs-modal-header">
            <h2>Current Openings</h2>
            <button className="modal-close-btn" onClick={() => setIsModalOpen(false)}>
              <X size={24} />
            </button>
          </div>
          
          <div className="jobs-list-container">
            {jobElements}
          </div>
        </div>
      </div>

    </div>
  );
}